import { sounds } from '@whitesharx/starfall-frontend-ui-kit';
import type { GameConfig, GameEvents } from '@whitesharx/starfall-frontend-chessground';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEmojis, useFlash } from './hooks';
import {
  persistGame,
  removePersistedGame,
  getStartGameData,
  getEndGameData,
  getEnemyData,
  getGameResult,
  formatGameResult,
  getInitialGameState,
  getFloatTimes,
  getTimers,
  getGameOverReasonByMoves,
  getInitialConfig,
  restoreGameConfig,
  getGameNumber,
  restoreGameState,
  getPersistedGame,
} from './utils/game';

import {
  ONBOARDING_GAMES_COUNT,
  COMPLETED_ONBOARDING_GAMES_COUNT_KEY,
  PREMOVE_KEY,
  GameMode,
  ONBOARDING_COMPLETED_AT_KEY,
} from './constants';
import { ActPhrase, ActSticker, GameOverReason, PlayerColor, OnboardingGameState } from './types';
import BotEmotions from './utils/botEmotions';
import { getBotThinkingTimeMS } from './utils/botThinkingTime';
import { useOnboardingServices } from '../CustomApp/OnboardingCustomApp';
import { CORE_EVENTS, TAP_EVENTS } from '~/src/services/analytics/contants';
import { useEvent } from '~/src/hooks/useEvent';
import { isDesktopBrowser } from '~/src/constants/env';
import { showInstallMobilePrompt, showInstallPrompt } from '~/src/utils/showInstallPrompt';
import { useAction, useAtom } from '@reatom/npm-react';
import {
  cgApiAtom,
  gameApiAtom,
  gameResultAtom,
  movesHistoryAtom,
  selectMoveAction,
  updateMovesHistoryAction,
} from './model';

let finishTimerId: NodeJS.Timeout | null = null;

const cancelFinishTimer = () => {
  if (finishTimerId) {
    clearTimeout(finishTimerId);
    finishTimerId = null;
  }
};

const useOnboardingGame = (isDesktop: boolean) => {
  const { Analytics, setMainResourcesLoaded, Adsense } = useOnboardingServices();

  const { shownFlash, flash } = useFlash();
  const [isOpenResignModal, setIsOpenResignModal] = useState(false);
  const [isOpenStalemateModal, setIsOpenStalemateModal] = useState(false);
  const [canUndoStalemate, setCanUndoStalemate] = useState(true);
  const [, setGameAPI] = useAtom(gameApiAtom);
  const [cgApi, setCgApi] = useAtom(cgApiAtom);
  const [game, setGame] = useState<OnboardingGameState>(restoreGameState);
  const [gameConfig, setGameConfig] = useState(restoreGameConfig);
  const [gameResult, setGameResult] = useAtom(gameResultAtom);
  const [movesHistory, setMovesHistory] = useAtom(movesHistoryAtom);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const updateMovesHistory = useAction(updateMovesHistoryAction);
  const selectMove = useAction(selectMoveAction);
  const [isOnboarding, setIsOnboarding] = useState(!localStorage.getItem('onboarding-completed'));
  const { showEnemyEmoji, showPlayerEmoji, playerEmoji, enemyEmoji, isEnemyMuted } = useEmojis();

  useEffect(() => {
    const persisted = getPersistedGame();

    if (persisted && persisted.moves.length > 0) {
      setMovesHistory(persisted.moves);
      selectMove(persisted.moves[persisted.moves.length - 1]);
    }

    if (persisted && persisted.result) {
      setGameResult(persisted.result);
    }

    return () => {
      removePersistedGame();
    };
  }, []);

  useEffect(() => {
    persistGame({ ...game, moves: movesHistory, result: gameResult });
  }, [game, movesHistory, gameResult]);

  useEffect(() => {
    if (cgApi && gameResult) {
      // if user refreshed the page and game is over we have to disable board
      cgApi.disable();
    }
  }, [cgApi, gameResult]);

  useEffect(() => {
    if (!isSearching && !game.enemy?.id) {
      setIsSearching(true);
    }
  }, [isSearching, game.enemy?.id]);

  useEffect(() => {
    if (isInitialized && isSearching) {
      const enemy = getEnemyData();
      setTimeout(() => {
        setGame((prev) => ({
          ...prev,
          enemy: {
            ...prev.enemy,
            ...enemy,
          },
          blackPlayerId: enemy.id,
        }));
      }, 1000);

      setTimeout(() => {
        // to fix an issue when all figures are rendered in 'a1' square
        cgApi?.enable();
        cgApi?.redrawAll();

        const initialGameState = getInitialGameState();

        setGame((prev) => ({
          ...prev,
          ...initialGameState,
          enemy: {
            ...initialGameState.enemy,
            ...enemy,
          },
          blackPlayerId: enemy.id,
        }));
        setIsSearching(false);

        Analytics.sendEvent(CORE_EVENTS.startGame, getStartGameData(initialGameState.mode));
      }, 2000);
    }
  }, [isInitialized, isSearching, cgApi]);

  useEffect(() => {
    cgApi?.destroy();

    setGameConfig(restoreGameConfig());
  }, [isDesktop]);

  useEffect(() => {
    setGameAPI({
      gameStarted: Boolean(cgApi),
    });
  }, [cgApi]);

  const continueOnboarding = () => {
    setGame((prev) => ({
      ...prev,
      enemy: undefined,
      downedFigures: undefined,
      isGameOver: false,
      result: undefined,
    }));
    setIsSearching(true);
    setCanUndoStalemate(true);

    setMovesHistory([]);
    setGameResult(null);

    setGameConfig(getInitialConfig());

    BotEmotions.chooseRandomPacks();
    BotEmotions.resetCount();
  };

  const initEvent = useEvent<GameEvents['init']>((api) => {
    sounds.load(['select', 'down', 'pick']);

    setIsInitialized(true);
    setMainResourcesLoaded(true);

    setGame((state) => ({
      ...state,
      downedFigures: api.getDownedFigures(),
    }));

    if (!game.isGameOver && game.turn === game.enemy?.color && game.fen) {
      const thinkingTime = getBotThinkingTimeMS({
        mode: game.mode,
        movesCount: movesHistory.length,
      });

      api.resumeBot();
      api.searchMove(thinkingTime);
    }
  });

  const moveEvent = useEvent<GameEvents['move']>(
    ({ inCheck, isCaptured, isLastMove, isBestMove, chances, turn, fen, ...data }) => {
      if (inCheck) {
        flash();
      }

      if (cgApi?.state.premovable.current) {
        cgApi?.playPremove();
      }

      cancelFinishTimer();

      sounds.play('down');

      if (isCaptured) {
        sounds.play('pick');
      }

      if (isLastMove) {
        setGame((state) => ({ ...state, downedFigures: cgApi?.getDownedFigures() }));
        return;
      }

      const isBotMove = game.player?.color === turn;

      if (!isEnemyMuted && !isLastMove) {
        BotEmotions.sendGreeting(movesHistory.length / 2, showEnemyEmoji);

        isBotMove
          ? BotEmotions.sendEmojiAfterBotMove(
              { winChance: chances[game.enemy?.color as PlayerColor], isCaptured },
              showEnemyEmoji
            )
          : BotEmotions.sendEmojiAfterPlayerMove(
              { winChance: chances[game.player?.color as PlayerColor], isCaptured, isBestMove },
              showEnemyEmoji
            );
      }

      const playerId = game.player.id!;

      const lastMove = {
        date: Date.now(),
        fen: data.previousFen,
        playerId: turn === game.player?.color ? game.enemy?.id! : playerId,
        san: data.san,
        from: data.from,
        to: data.to,
      };

      const moves = [...movesHistory, lastMove];
      const isGameOver = data.isGameOver || game.isGameOver;

      updateMovesHistory(movesHistory.concat({ ...lastMove, fen, beforeMoveFen: data.previousFen }));

      if (isDesktopBrowser && !isBotMove) {
        const playerMovesCount = moves.filter((move) => move.playerId === game.player.id).length;
        if (playerMovesCount === 5) {
          showInstallPrompt();
        }
      }

      if (!isBotMove) {
        const thinkingTime = getBotThinkingTimeMS({
          mode: game.mode,
          movesCount: moves.length,
        });
        cgApi?.searchMove(thinkingTime);
        localStorage.removeItem(PREMOVE_KEY);
      }

      const recalculateTimers = game.mode === GameMode.RAPID30 && !isGameOver;

      if (recalculateTimers) {
        const prevMove = movesHistory[movesHistory.length - 1];

        const lastMoveTime = lastMove.date - ((prevMove?.date as number) || game.createdAt!);

        const { whiteLeftTime, blackLeftTime } = getFloatTimes({
          turn,
          lastMoveTime,
          whiteLeftTime: game.whiteLeftTime,
          blackLeftTime: game.blackLeftTime,
        });

        const timers = getTimers({
          moves,
          playerColor: game.player?.color!,
          playerId,
          whiteLeftTime,
          blackLeftTime,
          createdAt: game.createdAt,
        });

        setGame((state) => ({
          ...state,
          whiteLeftTime,
          blackLeftTime,
          turn: isGameOver ? state.turn : turn,
          downedFigures: cgApi?.getDownedFigures(),
          player: {
            ...state.player,
            turnTime: timers.player,
            pause: isGameOver || !isBotMove,
          },
          enemy: {
            ...state.enemy,
            turnTime: timers.enemy,
            pause: isGameOver || isBotMove,
          },
          moves,
          fen,
        }));
      } else {
        setGame((state) => ({
          ...state,
          turn: isGameOver ? state.turn : turn,
          downedFigures: cgApi?.getDownedFigures(),
          moves,
          fen,
        }));
      }
    }
  );

  const groundEvents: GameEvents = {
    init: initEvent,
    figureClick: () => {
      sounds.play('select');
    },
    move: moveEvent,
    premoveSet: (data) => {
      localStorage.setItem(PREMOVE_KEY, JSON.stringify(data));
    },
    premoveUnset: () => {
      localStorage.removeItem(PREMOVE_KEY);
    },
    gameOver: useEvent((reason) => {
      const isPlayer = game.turn === 'w';

      if (isPlayer && reason === 'STALEMATE' && canUndoStalemate) {
        setIsOpenStalemateModal(true);
        setCanUndoStalemate(false);
      } else {
        gameOver(reason);
      }
    }),
  };

  const config = useMemo<GameConfig>(
    () => ({
      ...gameConfig,
      events: groundEvents,
    }),
    [gameConfig]
  );

  const gameOver = useEvent((reason: GameOverReason) => {
    cgApi?.disable();
    cgApi?.destroyBot();
    localStorage.removeItem(PREMOVE_KEY);

    const playerId = game.player!.id!;
    const enemyId = game.enemy!.id!;

    setIsOpenResignModal(false);

    const gameState = {
      ...game,
      isGameOver: true,
      player: {
        ...game.player,
        pause: true,
      },
      enemy: {
        ...game.enemy,
        pause: true,
      },
    };

    const result = formatGameResult(
      getGameResult({
        reason,
        playerId,
        enemyId,
        turn: game.turn!,
      }),
      game.player!,
      game.enemy!
    );

    setGameResult(result);

    setGame(gameState);

    Analytics.sendEvent(CORE_EVENTS.completeGame, getEndGameData(gameState, result));

    Adsense.showInterstitial('end-game');

    if (!isDesktopBrowser && getGameNumber() === 1) {
      showInstallMobilePrompt();
    }

    const completedGamesCount = Number(localStorage.getItem(COMPLETED_ONBOARDING_GAMES_COUNT_KEY)) + 1;

    localStorage.setItem(COMPLETED_ONBOARDING_GAMES_COUNT_KEY, String(completedGamesCount));

    if (completedGamesCount === ONBOARDING_GAMES_COUNT) {
      localStorage.setItem('onboarding-completed', 'true');
      localStorage.setItem(ONBOARDING_COMPLETED_AT_KEY, String(Date.now()));
      setIsOnboarding(false);
    }
  });

  const resign = useCallback(() => {
    Analytics.sendEvent(TAP_EVENTS.surrender, { type: 'confirm' });
    gameOver('SURRENDERED');
  }, [gameOver]);

  const closeResignModalWithAnalytics = () => {
    Analytics.sendEvent(TAP_EVENTS.surrender, { type: 'refuse' });
    setIsOpenResignModal(false);
  };

  const showPlayerEmojiOffline = (type: 'phrase' | 'sticker', payload: ActPhrase | ActSticker) => {
    showPlayerEmoji({ type, ...payload });

    if (type === 'phrase' && !isEnemyMuted && !game.isGameOver) {
      BotEmotions.sendReply(payload as ActPhrase, showEnemyEmoji);
    }
  };

  const onTimerEnd = useCallback(() => {
    if (isOpenStalemateModal) {
      gameOver('STALEMATE');
      setIsOpenStalemateModal(false);
    } else {
      gameOver(getGameOverReasonByMoves(movesHistory));
    }
  }, [gameOver, isOpenStalemateModal, movesHistory.length]);

  const undoStalemate = () => {
    cgApi?.enable();
    cgApi?.undoMove();
    setIsOpenStalemateModal(false);
  };

  return {
    isSearching,
    isOpenResignModal,
    openResignModal: () => setIsOpenResignModal(true),
    resign,
    closeResignModal: closeResignModalWithAnalytics,
    setCgApi,
    player: game.player,
    enemy: game.enemy,
    config,
    downedFigures: game.downedFigures,
    shownFlash,
    isGameOver: game.isGameOver,
    gameResult,
    showPlayerEmoji: showPlayerEmojiOffline,
    playerEmoji,
    enemyEmoji,
    game,
    moves: movesHistory,
    continueOnboarding,
    isInitialized,
    isOnboarding,
    onTimerEnd,
    isOpenStalemateModal,
    undoStalemate,
    closeStalemateModal: () => {
      gameOver('STALEMATE');
      setIsOpenStalemateModal(false);
    },
  };
};

export default useOnboardingGame;
